import type {
  LegalBlock,
  LegalDocument as CanonicalLegalDocument,
  LegalInline,
} from '@filmspo/legal-content';

function InlineContent({ inlines }: { inlines: readonly LegalInline[] }) {
  return (
    <>
      {inlines.map((inline, index) => {
        if (inline.kind === 'text')
          return <span key={`${inline.kind}-${index}`}>{inline.value}</span>;
        if (inline.kind === 'strong') {
          return (
            <strong key={`${inline.kind}-${index}`}>
              <InlineContent inlines={inline.children} />
            </strong>
          );
        }
        const external = inline.href.startsWith('http://') || inline.href.startsWith('https://');
        return (
          <a
            key={`${inline.kind}-${index}`}
            href={inline.href}
            target={external ? '_blank' : undefined}
            rel={external ? 'noopener noreferrer' : undefined}
          >
            {inline.label}
          </a>
        );
      })}
    </>
  );
}

function BlockContent({ block }: { block: LegalBlock }) {
  if (block.kind === 'heading') {
    const Heading = block.level === 2 ? 'h2' : 'h3';
    return <Heading id={block.anchor}>{block.text}</Heading>;
  }
  if (block.kind === 'paragraph') {
    return (
      <p>
        <InlineContent inlines={block.inlines} />
      </p>
    );
  }
  if (block.kind === 'list') {
    const List = block.ordered ? 'ol' : 'ul';
    return (
      <List>
        {block.items.map((item, index) => (
          <li key={index}>
            <InlineContent inlines={item} />
          </li>
        ))}
      </List>
    );
  }
  return (
    <div className="legal-table-wrapper">
      <table>
        <thead>
          <tr>
            {block.headers.map((header, index) => (
              <th key={index} scope="col">
                <InlineContent inlines={header} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {block.rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.map((cell, cellIndex) => (
                <td key={cellIndex}>
                  <InlineContent inlines={cell} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function LegalDocumentContent({
  document,
  includeTitle = true,
}: {
  document: CanonicalLegalDocument;
  includeTitle?: boolean;
}) {
  return (
    <article
      className="legal-document"
      data-legal-document-id={document.id}
      data-legal-document-type={document.type}
      data-legal-version={document.version}
      data-legal-content-sha256={document.publicContentSha256}
      data-legal-effective-date-status={document.effectiveDate.status}
      aria-labelledby={`${document.id}-title`}
    >
      {includeTitle ? (
        <h1 id={`${document.id}-title`} className="legal-title">
          {document.title}
        </h1>
      ) : (
        <h2 id={`${document.id}-title`} className="legal-document-title">
          {document.title}
        </h2>
      )}
      <dl className="legal-metadata">
        <div>
          <dt>Version</dt>
          <dd>{document.version}</dd>
        </div>
        <div>
          <dt>Effective date</dt>
          <dd>{document.effectiveDate.display}</dd>
        </div>
      </dl>
      <p className="legal-summary">{document.summary}</p>
      <div className="legal-blocks">
        {document.blocks.map((block, index) => (
          <BlockContent block={block} key={`${block.kind}-${index}`} />
        ))}
      </div>
    </article>
  );
}
